/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * CampaignTalkingPointsView — admin cockpit for the tenant's Stage 1
 * briefing copy (title + body cards shown to a canvasser before every
 * shift).
 *
 * Sister to CampaignIssuesView. Issues are short machine slugs; talking
 * points are longer prose blocks. Kept in a separate surface because the
 * edit affordances are different — slugs need url-safe validation, talking
 * points need a textarea and a preview.
 *
 * Role walls (enforced backend-side):
 *
 *   - GET  /canvass/talking-points        any CANVASSER+ (read-only)
 *   - POST /canvass/talking-points        ISSUE_ADMIN_ROLES (ADMIN / ORG_MANAGER / MANAGER)
 *   - PATCH /canvass/talking-points/{id}  ISSUE_ADMIN_ROLES
 *
 * Same optimistic-UI-then-403 pattern as CampaignIssuesView — a canvasser
 * who lands here sees read-only data, and every mutate surfaces a red
 * banner from the server.
 *
 * Sort order: (is_active DESC, display_order ASC, id ASC) client-side so
 * active points float above deactivated ones regardless of display_order
 * density.
 */
import React, { useEffect, useMemo, useState } from 'react';

import {
  createTalkingPoint,
  formatCanvassError,
  listTalkingPoints,
  updateTalkingPoint,
} from '@/api/canvass';
import type {
  CampaignTalkingPointCreate,
  CampaignTalkingPointRead,
  CampaignTalkingPointUpdate,
} from '@/types/canvass';
import { InfoTip } from '@/components/InfoTip';
import {
  OnboardingModal,
  OnboardingHelpButton,
} from '@/components/OnboardingModal';

function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function CampaignTalkingPointsView({
  onBack,
}: {
  onBack: () => void;
}) {
  const [includeInactive, setIncludeInactive] = useState(true);
  const [points, setPoints] = useState<CampaignTalkingPointRead[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [forceOnboard, setForceOnboard] = useState(false);

  async function refresh() {
    setPoints(null);
    setError(null);
    try {
      const rows = await listTalkingPoints(includeInactive);
      setPoints(rows);
    } catch (e) {
      setError(formatCanvassError(e));
      setPoints([]);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive]);

  const sortedPoints = useMemo(() => {
    if (!points) return [];
    return [...points].sort((a, b) => {
      if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
      if (a.display_order !== b.display_order)
        return a.display_order - b.display_order;
      return a.id - b.id;
    });
  }, [points]);

  async function handleToggleActive(tp: CampaignTalkingPointRead) {
    setBusyId(tp.id);
    setError(null);
    try {
      await updateTalkingPoint(tp.id, { is_active: !tp.is_active });
      await refresh();
    } catch (e) {
      setError(formatCanvassError(e));
    } finally {
      setBusyId(null);
    }
  }

  async function handleSaveEdit(
    tp: CampaignTalkingPointRead,
    patch: CampaignTalkingPointUpdate,
  ) {
    setBusyId(tp.id);
    setError(null);
    try {
      await updateTalkingPoint(tp.id, patch);
      setEditingId(null);
      await refresh();
    } catch (e) {
      setError(formatCanvassError(e));
    } finally {
      setBusyId(null);
    }
  }

  async function handleCreate(
    body: CampaignTalkingPointCreate,
  ): Promise<boolean> {
    setError(null);
    try {
      await createTalkingPoint(body);
      await refresh();
      return true;
    } catch (e) {
      setError(formatCanvassError(e));
      return false;
    }
  }

  return (
    <div className="space-y-4">
      <OnboardingModal
        viewKey="campaigntalkingpointsview"
        title="Campaign Talking Points"
        intro="Manage the tenant-scoped briefing copy that canvassers read before each shift. Each talking point is a short title plus a prose body — candidate positions, common objections and rebuttals, logistical reminders. Active points land on every Stage 1 briefing ordered by display_order."
        termIds={['talking_points', 'briefing', 'knock']}
        workflows={[
          "Author the two-three things every canvasser should say at every door — keep bodies under a paragraph.",
          'Deactivate obsolete copy instead of deleting so historical briefings stay reconstructible.',
          'Use display_order to float priority messaging to the top of the briefing card.',
        ]}
        forceOpen={forceOnboard}
        onClose={() => setForceOnboard(false)}
      />

      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-fg-muted uppercase tracking-wide">
            Admin · canvass
          </div>
          <h1 className="text-3xl font-bold text-fg-primary mt-1 flex items-center gap-2">
            Talking Points <InfoTip termId="talking_points" />
          </h1>
          <p className="text-fg-muted text-sm mt-1">
            Stage 1 briefing copy. Authored by managers, read by every canvasser.
          </p>
        </div>
        <div className="flex gap-2">
          <OnboardingHelpButton onClick={() => setForceOnboard(true)} />
        </div>
      </div>

      {error && <ErrorBanner message={error} onClear={() => setError(null)} />}

      <CreateTalkingPointForm onSubmit={handleCreate} />

      <div className="bg-bg-raised rounded-lg border border-border-base p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-xs text-fg-muted uppercase tracking-wide">
            {sortedPoints.length} talking point
            {sortedPoints.length === 1 ? '' : 's'}
          </div>
          <div className="flex gap-2 items-center">
            <label className="text-xs text-fg-secondary flex items-center gap-2">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                className="min-h-11 min-w-11 sm:min-h-0 sm:min-w-0"
              />
              Show inactive
            </label>
            <button
              onClick={refresh}
              className="px-3 py-1 rounded text-xs font-semibold bg-bg-elevated hover:bg-border-subtle text-fg-secondary inline-flex items-center justify-center min-h-11 sm:min-h-0"
            >
              ↻ Refresh
            </button>
          </div>
        </div>

        {points === null && (
          <div className="text-fg-muted text-sm py-6 text-center">
            Loading talking points…
          </div>
        )}
        {points !== null && sortedPoints.length === 0 && (
          <div className="text-fg-muted text-sm py-6 text-center">
            No talking points yet. Add your first one above — briefings will
            show a "no talking points authored" notice until you do.
          </div>
        )}
        {sortedPoints.length > 0 && (
          <div className="space-y-2">
            {sortedPoints.map((tp) =>
              editingId === tp.id ? (
                <EditTalkingPointRow
                  key={tp.id}
                  tp={tp}
                  busy={busyId === tp.id}
                  onSave={(patch) => handleSaveEdit(tp, patch)}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <TalkingPointRow
                  key={tp.id}
                  tp={tp}
                  busy={busyId === tp.id}
                  onEdit={() => setEditingId(tp.id)}
                  onToggleActive={() => handleToggleActive(tp)}
                />
              ),
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create form
// ---------------------------------------------------------------------------

function CreateTalkingPointForm({
  onSubmit,
}: {
  onSubmit: (body: CampaignTalkingPointCreate) => Promise<boolean>;
}) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [displayOrder, setDisplayOrder] = useState<string>('100');
  const [saving, setSaving] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    setSaving(true);
    const ok = await onSubmit({
      title: title.trim(),
      body: body.trim(),
      is_active: true,
      display_order: Number.isFinite(parseInt(displayOrder, 10))
        ? parseInt(displayOrder, 10)
        : 100,
    });
    setSaving(false);
    if (ok) {
      setTitle('');
      setBody('');
      setDisplayOrder('100');
    }
  }

  return (
    <form
      onSubmit={submit}
      className="bg-bg-raised rounded-lg border border-border-base p-4 space-y-2"
    >
      <div className="text-xs text-fg-muted uppercase tracking-wide">
        Add a new talking point
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
        <div className="sm:col-span-5">
          <div className="text-[10px] text-fg-muted uppercase mb-1">
            Title (card headline)
          </div>
          <input
            className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
            placeholder="e.g. Taxes — three-sentence version"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={saving}
          />
        </div>
        <div className="sm:col-span-1">
          <label htmlFor="talking-point-new-order" className="block text-[10px] text-fg-muted uppercase mb-1">
            Order
          </label>
          <input
            id="talking-point-new-order"
            type="number"
            className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
            value={displayOrder}
            onChange={(e) => setDisplayOrder(e.target.value)}
            disabled={saving}
          />
        </div>
      </div>
      <div>
        <div className="text-[10px] text-fg-muted uppercase mb-1">
          Body (plain text — no markdown rendering)
        </div>
        <textarea
          className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm font-mono"
          rows={4}
          placeholder="What the canvasser should say or internalize. Keep it concise — canvassers skim."
          value={body}
          onChange={(e) => setBody(e.target.value)}
          disabled={saving}
        />
      </div>
      <button
        type="submit"
        disabled={saving || !title.trim() || !body.trim()}
        className="bg-accent hover:bg-accent-hover disabled:opacity-40 text-white font-bold px-4 py-2 rounded text-sm min-h-11 sm:min-h-0"
      >
        {saving ? 'Saving…' : '+ Add talking point'}
      </button>
      <div className="text-[11px] text-fg-muted">
        Line breaks are preserved when the briefing renders. Keep bodies
        under ~150 words so canvassers can read them on a phone.
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Row display / edit
// ---------------------------------------------------------------------------

function TalkingPointRow({
  tp,
  busy,
  onEdit,
  onToggleActive,
}: {
  tp: CampaignTalkingPointRead;
  busy: boolean;
  onEdit: () => void;
  onToggleActive: () => void;
}) {
  return (
    <div
      className={`bg-bg-base rounded border px-3 py-3 flex items-start justify-between gap-3 ${
        tp.is_active ? 'border-border-base' : 'border-border-base opacity-60'
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-fg-primary truncate">{tp.title}</span>
          {!tp.is_active && (
            <span className="text-[10px] text-fg-muted bg-bg-raised border border-border-base rounded px-1.5 py-0.5">
              INACTIVE
            </span>
          )}
        </div>
        <div className="mt-1 text-sm text-fg-secondary whitespace-pre-wrap leading-snug">
          {tp.body}
        </div>
        <div className="text-xs text-fg-muted mt-1 font-mono">
          order {tp.display_order} · updated {formatTime(tp.updated_at)}
        </div>
      </div>
      <div className="flex flex-col gap-2 items-stretch shrink-0">
        <button
          onClick={onToggleActive}
          disabled={busy}
          className={`text-xs font-semibold px-3 py-1 rounded ${
            tp.is_active
              ? 'bg-bg-elevated hover:bg-border-subtle text-fg-secondary'
              : 'bg-status-success-bg hover:bg-status-success-bg/80 text-white'
          } disabled:opacity-40`}
        >
          {tp.is_active ? 'Deactivate' : 'Reactivate'}
        </button>
        <button
          onClick={onEdit}
          disabled={busy}
          className="text-xs font-semibold px-3 py-1 rounded bg-bg-elevated hover:bg-border-subtle text-fg-secondary disabled:opacity-40"
        >
          Edit
        </button>
      </div>
    </div>
  );
}

function EditTalkingPointRow({
  tp,
  busy,
  onSave,
  onCancel,
}: {
  tp: CampaignTalkingPointRead;
  busy: boolean;
  onSave: (patch: CampaignTalkingPointUpdate) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(tp.title);
  const [body, setBody] = useState(tp.body);
  const [order, setOrder] = useState<string>(String(tp.display_order));

  return (
    <div className="bg-bg-base rounded border border-accent px-3 py-3 space-y-2">
      <div className="text-xs text-fg-muted uppercase tracking-wide font-mono">
        editing #{tp.id}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
        <div className="sm:col-span-5">
          <div className="text-[10px] text-fg-muted uppercase mb-1">Title</div>
          <input
            className="w-full bg-bg-raised border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="sm:col-span-1">
          <div className="text-[10px] text-fg-muted uppercase mb-1">Order</div>
          <input
            type="number"
            className="w-full bg-bg-raised border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
            value={order}
            onChange={(e) => setOrder(e.target.value)}
            disabled={busy}
          />
        </div>
      </div>
      <div>
        <div className="text-[10px] text-fg-muted uppercase mb-1">Body</div>
        <textarea
          className="w-full bg-bg-raised border border-border-base rounded px-3 py-2 text-fg-primary text-sm font-mono"
          rows={5}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          disabled={busy}
        />
      </div>
      <div className="flex gap-2">
        <button
          disabled={busy || !title.trim() || !body.trim()}
          onClick={() =>
            onSave({
              title: title.trim(),
              body: body.trim(),
              display_order: Number.isFinite(parseInt(order, 10))
                ? parseInt(order, 10)
                : tp.display_order,
            })
          }
          className="bg-accent hover:bg-accent-hover disabled:opacity-40 text-white font-bold px-4 py-2 rounded text-sm"
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button
          onClick={onCancel}
          disabled={busy}
          className="bg-bg-elevated hover:bg-border-subtle disabled:opacity-40 text-fg-secondary font-semibold px-4 py-2 rounded text-sm"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error banner
// ---------------------------------------------------------------------------

function ErrorBanner({
  message,
  onClear,
}: {
  message: string;
  onClear: () => void;
}) {
  return (
    <div className="bg-status-danger-bg/40 border border-status-danger-bg rounded p-3 flex items-start justify-between gap-3">
      <div className="text-sm text-status-danger">{message}</div>
      <button
        onClick={onClear}
        className="text-status-danger hover:text-fg-primary text-xs"
      >
        dismiss
      </button>
    </div>
  );
}

export default CampaignTalkingPointsView;
